import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, useTheme } from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import { ReactNode, useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { MapRenderContext } from 'src/contexts/MapRenderContext'

const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window)
}

const SideSheet = ({ children }: { children: ReactNode }) => {
    const bottomSheetRef = useRef<HTMLDivElement | null>(null)
    const theme = useTheme()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const { map, mapRef } = useContext(MapRenderContext)

    // If we just switched from portrait or if the BottomSheet was loaded for whatever reason, force the map to full height
    useEffect(() => {
        mapRef!.current.style.height = '100%'
        map!.current!.resize()
    }, [map, mapRef])

    return (
        <div
            ref={bottomSheetRef}
            style={{
                position: 'absolute',
                left: 0,
                bottom: `calc(56px + env(safe-area-inset-bottom))`, // Offset for bottom nav height
                width: '400px',
                height: 'calc(100% - 56px - env(safe-area-inset-bottom))',
                paddingLeft: 'env(safe-area-inset-left)',
                paddingTop: isIOS() ? 56 : 0,
                background: `color-mix(in srgb, ${theme.palette.background.default} 90%, transparent)`,
                backdropFilter: 'blur(24px) saturate(1.6)',
                WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
                borderRadius: '0 16px 16px 0',
                boxShadow: '4px 0 32px rgba(0,0,0,0.1)',
                zIndex: 3,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <IconButton
                aria-label={t('Close sidebar')}
                size="small"
                onClick={() => navigate({ to: '/' })}
                sx={{
                    'position': 'absolute',
                    'top': 8,
                    'right': -18,
                    'backgroundColor': 'primary.main',
                    'color': '#fff',
                    '&:hover': {
                        backgroundColor: 'primary.dark',
                    },
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ overflowY: 'auto' }}>
                {children}
            </Box>
        </div>
    )
}

export default SideSheet
